import React, { useEffect, useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Search, FolderOpen, LayoutGrid, List, UserMinus, RotateCcw, Check, KeyRound, FileText, AlertTriangle, Send } from "lucide-react";
import api, { fmtDate, fmtEur } from "../lib/api";
import { ExportButton } from "./ExportButton";
import { exportCheckinCredenziali } from "../lib/exporter";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useFirma } from "../context/FirmaContext";
import { structForMonth } from "../lib/structure";
import StatusBadge from "./StatusBadge";
import { IdBadge } from "./IdBadge";
import QuickEventDialog from "./QuickEventDialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";
import { toast } from "sonner";
import { OperatoreResponsabileSelect } from "./OperatoreResponsabileSelect";
import { EVENTO_LABEL, eventoColor } from "../lib/eventiSanitari";
import { useOpConformita, fmtYm } from "./OperatoriConformitaPanel";


const ALERT_DOT_DEFS = [
  { key: "evento", title: "Evento sanitario in corso", cls: "bg-red-500" },
  { key: "parametri", title: "Parametri critici (ultimi 3 giorni)", cls: "bg-rose-600" },
  { key: "alvo", title: "Alvo: 3+ giorni senza evacuazione", cls: "bg-orange-500" },
  { key: "cadute", title: "Caduta negli ultimi 7 giorni", cls: "bg-amber-500" },
];

export default function DataModule({ entity, config, filterFn, createDefaults, month }) {
  const { selectedStructure, can, structures, setSelectedStructure, opSections, companyLogo } = useAuth();
  const { activeOperatore } = useFirma();
  const permOk = (perm) => {
    if (!perm) return true;
    if (activeOperatore) return (opSections || []).includes(perm);
    return can(perm);
  };
  const structMap = Object.fromEntries((structures || []).map((s) => [s.id, s]));
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [delId, setDelId] = useState(null);
  const [pinRow, setPinRow] = useState(null);
  const [pinBusy, setPinBusy] = useState(false);
  const [pinAllOpen, setPinAllOpen] = useState(false);
  const [pinAllBusy, setPinAllBusy] = useState(false);
  const [pinAllResult, setPinAllResult] = useState(null);
  const [scadConfirm, setScadConfirm] = useState(null);
  const [toggleConfirm, setToggleConfirm] = useState(null);
  const [dischargeRow, setDischargeRow] = useState(null);
  const [deceduto, setDeceduto] = useState(false);
  const [dischG, setDischG] = useState("");
  const [dischM, setDischM] = useState("");
  const [dischA, setDischA] = useState(String(new Date().getFullYear()));
  const dischargeDate = (dischG && dischM && dischA) ? `${dischA}-${dischM}-${dischG}` : "";
  const canDischarge = entity === "ospiti" || entity === "operatori";
  const isRinuncia = (row) => entity === "pagamenti" ? !!row._rinuncia : !!(row.rinuncia_caparra || row.presenza === "rinuncia");
  const dischField = entity === "operatori" ? "data_cessazione" : "data_dimissione";
  const startField = entity === "operatori" ? "data_assunzione" : "data_ingresso";
  const [quick, setQuick] = useState(null);
  const [ospiti, setOspiti] = useState([]);
  const [view, setView] = useState("lista");
  const [lf, setLf] = useState({});
  const [sel, setSel] = useState([]);

  const key = ["mod", entity, (config.companyLevel || config.structByMonth) ? "all" : selectedStructure];
  const { data = [], isLoading } = useQuery({
    queryKey: key,
    queryFn: async () => (await api.get(`/${entity}`, { params: { structure_id: (config.companyLevel || config.structByMonth) ? "all" : selectedStructure } })).data,
  });

  const needsOspite = config.fields.some((f) => f.type === "ospite");
  const needsFornitori = config.fields.some((f) => f.type === "fornitore");
  const { data: fornitori = [] } = useQuery({
    queryKey: ["fornitori-opts"],
    queryFn: async () => (await api.get(`/fornitori`, { params: { structure_id: "all" } })).data,
    enabled: needsFornitori,
  });
  useEffect(() => {
    if (needsOspite && open) {
      api.get(`/ospiti`, { params: { structure_id: selectedStructure } }).then((r) => setOspiti(r.data));
    }
  }, [open, selectedStructure, needsOspite]);

  // Arricchimento rette per la vista Ospiti (colonne visibili solo con permesso "rette")
  const showRette = !!(config.rosterRette && permOk("rette"));
  const { data: pagMese = [] } = useQuery({
    queryKey: ["osp-pag", config.structByMonth ? "all" : selectedStructure, month],
    enabled: showRette && !!month,
    queryFn: async () => (await api.get(`/pagamenti`, { params: { structure_id: config.structByMonth ? "all" : selectedStructure } })).data,
  });
  const { data: retteBase = [] } = useQuery({
    queryKey: ["osp-rette", config.structByMonth ? "all" : selectedStructure],
    enabled: showRette,
    queryFn: async () => (await api.get(`/rette`, { params: { structure_id: config.structByMonth ? "all" : selectedStructure } })).data,
  });
  const { data: ospitiForBadge = [] } = useQuery({
    queryKey: ["osp-badge", selectedStructure],
    enabled: !!config.showDischargeBadge,
    queryFn: async () => (await api.get(`/ospiti`, { params: { structure_id: selectedStructure } })).data,
  });
  const { data: infOggi = [] } = useQuery({
    queryKey: ["osp-inf-badge", selectedStructure],
    enabled: entity === "ospiti",
    queryFn: async () => { try { return (await api.get("/dashboard/infettive-oggi", { params: { structure_id: selectedStructure } })).data; } catch { return []; } },
  });
  const { data: alvoAlertDots = [] } = useQuery({
    queryKey: ["osp-alvo-dots", selectedStructure],
    enabled: entity === "ospiti",
    queryFn: async () => { try { return (await api.get("/dashboard/alvo-alert", { params: { structure_id: selectedStructure } })).data; } catch { return []; } },
  });
  const { data: criticiDots = { ospiti: [] } } = useQuery({
    queryKey: ["osp-critici-dots", selectedStructure],
    enabled: entity === "ospiti",
    queryFn: async () => { try { return (await api.get("/dashboard/parametri-critici", { params: { structure_id: selectedStructure, giorni: 3 } })).data; } catch { return { ospiti: [] }; } },
  });
  const { data: caduteDots = [] } = useQuery({
    queryKey: ["osp-cadute-dots", selectedStructure],
    enabled: entity === "ospiti",
    queryFn: async () => { try { return (await api.get("/dashboard/cadute-oggi", { params: { structure_id: selectedStructure, giorni: 7 } })).data; } catch { return []; } },
  });
  const infMap = useMemo(() => { const m = {}; (infOggi || []).forEach((x) => { if (x.ospite_id) m[x.ospite_id] = x; }); return m; }, [infOggi]);
  const { data: docObbl = null } = useQuery({ queryKey: ["osp-doc-obbl"], enabled: entity === "ospiti", queryFn: async () => { try { return (await api.get("/documenti/obbligatori-ospiti")).data; } catch { return null; } } });
  const [soloMancanti, setSoloMancanti] = useState(false);
  const { data: opConf = [] } = useOpConformita(entity === "operatori");
  const opConfMap = useMemo(() => Object.fromEntries((opConf || []).map((x) => [x.id, x])), [opConf]);
  const docMancanti = (r) => {
    if (entity === "operatori") { const c = opConfMap[r.id]; return c ? [c.manca_contratto && "Manca contratto", c.buste_ritardo.length && `Buste in ritardo (${c.buste_ritardo.length})`].filter(Boolean) : []; }
    return entity === "ospiti" && docObbl ? [["certificato", "Manca certificato"], ["contratto", "Manca contratto"], ["terapia", "Manca terapia"]].filter(([k]) => !(docObbl[r.id] || []).includes(k)).map(([, l]) => l) : [];
  };
  const DocBadges = ({ r }) => (entity === "ospiti" ? null : docMancanti(r).map((l) => <span key={l} title={entity === "operatori" && l.startsWith("Buste") ? (opConfMap[r.id]?.buste_ritardo || []).map(fmtYm).join(", ") : undefined} className="inline-block mt-0.5 mr-1 rounded px-1.5 py-[1px] text-[9px] font-bold leading-tight whitespace-nowrap bg-red-100 text-red-700 border border-red-200" data-testid={`${entity === "operatori" ? "op" : "osp"}-doc-mancante-${r.id}-${l.split(" ")[l.startsWith("Buste") ? 0 : 1].toLowerCase()}`}>{l}</span>));
  const alertDots = useMemo(() => {
    const m = {};
    const add = (id, key) => { if (!id) return; (m[id] = m[id] || {})[key] = true; };
    (infOggi || []).forEach((x) => add(x.ospite_id, "evento"));
    (criticiDots?.ospiti || []).forEach((x) => add(x.ospite_id, "parametri"));
    (alvoAlertDots || []).forEach((x) => add(x.ospite_id, "alvo"));
    (caduteDots || []).forEach((x) => add(x.ospite_id, "cadute"));
    return m;
  }, [infOggi, criticiDots, alvoAlertDots, caduteDots]);
  const rows = useMemo(() => {
    let out = data;
    if (showRette) {
      const pagByOsp = {};
      pagMese.forEach((p) => { if (p.mese === month) pagByOsp[p.ospite_id] = p; });
      const prevByOsp = {};
      retteBase.forEach((r) => { if (r.ospite_id) prevByOsp[r.ospite_id] = Number(r.importo || 0); });
      out = out.map((g) => {
        const p = pagByOsp[g.id];
        const prevista = p ? Number(p.retta_prevista || 0) : (prevByOsp[g.id] ?? null);
        const incassato = p ? Number(p.importo_incassato || 0) : 0;
        const stato_retta = p ? p.stato : (prevista != null ? "da incassare" : null);
        return { ...g, retta_prevista: prevista, incassato, stato_retta };
      });
    }
    if (config.showDischargeBadge) {
      const byId = {};
      ospitiForBadge.forEach((o) => { byId[o.id] = o; });
      out = out.map((r) => {
        const o = byId[r.ospite_id] || {};
        const live = [o.nome, o.cognome].filter(Boolean).join(" ").trim();
        const cognome = o.cognome || (r.ospite_nome || "").trim().split(/\s+/).slice(-1)[0] || "";
        return { ...r, _dimesso: o.data_dimissione || null, _periodi: o.periodi || null, _nome_live: live || null, _cognome: cognome, _blocco_mesi: o.blocco_posto_mesi || {}, _registro_mesi: o.registro_mesi || {}, _rinuncia: !!(o.rinuncia_caparra || o.presenza === "rinuncia") };
      });
    }
    return out;
  }, [data, pagMese, retteBase, month, showRette, ospitiForBadge]);

  const visibleCols = (config.columns || []).filter((c) => permOk(c.perm));
  const bulkOn = config.bulkPay && (!config.rosterRette || showRette);
  const upsertRetta = (row, patch) => api.post("/pagamenti/upsert", { ospite_id: row.id, mese: month, structure_id: config.structByMonth ? structForMonth(row, month) : row.structure_id, ...patch }).then(() => { qc.invalidateQueries({ queryKey: ["osp-pag"] }); qc.invalidateQueries({ queryKey: ["osp-rette"] }); });
  const saveNum = (row, col, value) => {
    const num = value === "" ? 0 : Number(value);
    if (config.rosterRette) {
      const field = col.key === "incassato" ? "importo_incassato" : "retta_prevista";
      const extra = field === "retta_prevista" ? { set_base: true } : {};
      return upsertRetta(row, { [field]: num, ...extra });
    }
    return patchRow(row, { [col.key]: num });
  };

  const openNew = () => {
    if (config.newNavigate) { navigate(config.newNavigate); return; }
    setEditing(null);
    const init = { ...(createDefaults || {}) };
    if (!config.companyLevel && selectedStructure !== "all") init.structure_id = selectedStructure;
    setForm(init); setOpen(true);
  };
  const openEdit = (row) => { setEditing(row); setForm(row); setOpen(true); };

  const save = async () => {
    const payload = { ...form };
    if (Array.isArray(payload.structure_ids) && payload.structure_ids.length) payload.structure_id = payload.structure_ids[0];
    if (payload.structure_id == null && !config.companyLevel && selectedStructure !== "all") payload.structure_id = selectedStructure;
    if (!config.companyLevel && !payload.structure_id) { toast.error("Seleziona una struttura specifica in alto per creare il record."); return; }
    const missing = (config.fields || []).filter((f) => f.req).filter((f) => f.type === "ospite" ? !payload.ospite_id : (f.type === "struttura" || f.type === "struttura_split") ? !payload.structure_id : f.type === "strutture" ? !(payload.structure_ids && payload.structure_ids.length) : !payload[f.key]);
    if (missing.length) { toast.error(`Compila i campi obbligatori: ${missing.map((f) => f.label).join(", ")}`); return; }
    if (!editing && payload.structure_id === "__all__") {
      const list = structures || [];
      if (!list.length) { toast.error("Nessuna struttura disponibile"); return; }
      const n = list.length;
      const numFields = (config.fields || []).filter((f) => f.type === "number").map((f) => f.key);
      try {
        for (let i = 0; i < n; i++) {
          const rec = { ...payload, structure_id: list[i].id };
          delete rec.struttura;
          numFields.forEach((k) => {
            const tot = Number(payload[k] || 0);
            const each = Math.round((tot / n) * 100) / 100;
            rec[k] = i === n - 1 ? Math.round((tot - each * (n - 1)) * 100) / 100 : each;
          });
          rec.ripartizione = `Quota ${i + 1}/${n} — spesa ripartita in parti uguali su ${n} strutture`;
          await api.post(`/${entity}`, rec);
        }
        toast.success(`Spesa ripartita in parti uguali su ${n} strutture`);
        setOpen(false);
        qc.invalidateQueries({ queryKey: ["mod", entity] });
      } catch (e) { toast.error(e.response?.data?.detail || "Errore nella ripartizione"); }
      return;
    }
    if (editing && entity === "operatori") {
      const oldS = editing.scadenza_contratto || "";
      const newS = payload.scadenza_contratto || "";
      if (oldS !== newS) { setScadConfirm({ payload, oldS, newS }); return; }
    }
    await persistEdit(payload);
  };
  const persistEdit = async (payload) => {
    try {
      if (editing) await api.put(`/${entity}/${editing.id}`, payload);
      else await api.post(`/${entity}`, payload);
      toast.success("Salvato con successo");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["mod", entity] });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
    }
  };
  const doDelete = async () => {
    try {
      await api.delete(`/${entity}/${delId}`);
      toast.success("Eliminato");
      setDelId(null);
      qc.invalidateQueries({ queryKey: ["mod", entity] });
    } catch { toast.error("Errore"); }
  };
  const doSendPin = async () => {
    if (!pinRow) return;
    setPinBusy(true);
    try {
      const { data } = await api.post(`/operatori/${pinRow.id}/invia-pin`, { app_url: window.location.origin });
      toast.success(`PIN inviato a ${data.email}`);
      setPinRow(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Invio non riuscito"); }
    finally { setPinBusy(false); }
  };
  const doSendPinAll = async () => {
    setPinAllBusy(true);
    try {
      const sid = (lf?.struttura && lf.struttura !== "all") ? lf.struttura : "all";
      const { data } = await api.post(`/operatori/invia-pin-massivo`, { app_url: window.location.origin, structure_id: sid });
      setPinAllResult(data);
      setPinAllOpen(false);
      if (data.inviate > 0) toast.success(`PIN inviato a ${data.inviate} operatori`);
      else toast.info("Nessun PIN inviato");
    } catch (e) { toast.error(e.response?.data?.detail || "Invio non riuscito"); }
    finally { setPinAllBusy(false); }
  };
  const doCheckinPdf = async (row) => {
    try {
      const { data } = await api.get(`/operatori/${row.id}/checkin-credenziali`);
      exportCheckinCredenziali({
        operatore: data.operatore_nome || [row.cognome, row.nome].filter(Boolean).join(" "),
        struttura: data.struttura_nome,
        link: window.location.origin,
        email: data.email,
        password: data.password,
        pin: data.pin,
        logo: companyLogo,
      });
      if (!data.email || !data.password) toast.warning("Account di struttura non trovato: email/password vuote nel PDF");
      else toast.success("PDF credenziali generato");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nella generazione"); }
  };
  const openDischarge = (row) => {
    setDischargeRow(row);
    setDeceduto(!!row?.deceduto);
    const existing = row?.[dischField];
    if (existing && existing.length >= 10) {
      setDischA(existing.slice(0, 4)); setDischM(existing.slice(5, 7)); setDischG(existing.slice(8, 10));
    } else {
      setDischA(String(new Date().getFullYear())); setDischM(""); setDischG("");
    }
  };
  const doDischarge = async (clear) => {
    try {
      await api.post(`/${entity}/${dischargeRow.id}/dimissione`, { data_dimissione: clear ? null : dischargeDate, ...(entity === "ospiti" ? { deceduto: !clear && deceduto } : {}) });
      toast.success(clear ? "Annullato" : (entity === "operatori" ? "Operatore cessato" : deceduto ? "Ospite segnato come deceduto/a" : "Ospite dimesso"));
      setDischargeRow(null);
      qc.invalidateQueries({ queryKey: ["mod", entity] });
      qc.invalidateQueries({ queryKey: ["mod", "pagamenti"] });
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const doReadmit = async () => {
    try {
      await api.post(`/${entity}/${dischargeRow.id}/riammissione`, { data_ingresso: dischargeDate });
      toast.success(entity === "operatori" ? "Operatore riassunto (nuovo periodo)" : "Ospite riammesso (nuovo periodo)");
      setDischargeRow(null);
      qc.invalidateQueries({ queryKey: ["mod", entity] });
      qc.invalidateQueries({ queryKey: ["mod", "pagamenti"] });
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const patchRow = async (row, changes) => {
    let patch = { ...row, ...changes };
    if (entity === "pagamenti") {
      const prev = Number(patch.retta_prevista || 0);
      if ("stato" in changes) {
        if (changes.stato === "pagato") patch.importo_incassato = prev;
        else if (changes.stato === "da incassare") patch.importo_incassato = 0;
      } else if ("retta_prevista" in changes || "importo_incassato" in changes) {
        const inc = Number(patch.importo_incassato || 0);
        patch.stato = inc <= 0 ? "da incassare" : (prev > 0 && inc >= prev ? "pagato" : "parziale");
      }
      patch.differenza = Number(patch.retta_prevista || 0) - Number(patch.importo_incassato || 0);
    }
    try {
      await api.put(`/${entity}/${row.id}`, patch);
      if (entity === "pagamenti" && "retta_prevista" in changes && row.ospite_id) {
        await api.post("/rette/sync", { ospite_id: row.ospite_id, importo: Number(patch.retta_prevista || 0), structure_id: row.structure_id });
      }
      qc.invalidateQueries({ queryKey: ["mod", entity] });
    }
    catch { toast.error("Errore aggiornamento"); }
  };
  const cycleStatus = (row, col) => {
    const opts = col.statusOptions || [];
    const next = opts[(opts.indexOf(row[col.key]) + 1) % opts.length];
    patchRow(row, { [col.key]: next }).then(() => toast.success(`Stato: ${next}`));
  };
  const toggleSel = (id) => setSel((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const bulkMarkPaid = async () => {
    const rows2 = filtered.filter((r) => sel.includes(r.id));
    if (!rows2.length) return;
    try {
      if (config.rosterRette) {
        await Promise.all(rows2.map((r) => api.post("/pagamenti/upsert", { ospite_id: r.id, mese: month, structure_id: config.structByMonth ? structForMonth(r, month) : r.structure_id, stato: "pagato" })));
        await qc.invalidateQueries({ queryKey: ["osp-pag"] });
      } else {
        await Promise.all(rows2.map((r) => {
          const prev = Number(r.retta_prevista || 0);
          return api.put(`/${entity}/${r.id}`, { ...r, stato: "pagato", importo_incassato: prev, differenza: 0 });
        }));
        await qc.invalidateQueries({ queryKey: ["mod", entity] });
      }
      toast.success(`${rows2.length} rette segnate come pagate`);
      setSel([]);
    } catch { toast.error("Errore aggiornamento"); }
  };
  const setStatoRow = async (row, stato) => {
    let motivo = "";
    if (stato === "uscita" || stato === "ospedale") { motivo = window.prompt(stato === "ospedale" ? "Motivo del ricovero (opzionale):" : "Motivo dell'uscita (opzionale):") || ""; }
    try {
      await api.post(`/ospiti/${row.id}/stato`, { stato, motivo });
      qc.invalidateQueries({ queryKey: ["mod", "ospiti"] });
      qc.invalidateQueries({ queryKey: ["cal-full"] });
      qc.invalidateQueries({ queryKey: ["cal"] });
      toast.success("Stato aggiornato");
    } catch { toast.error("Errore"); }
  };
  const toggleBloccoPosto = async (row) => {
    const ospiteId = entity === "pagamenti" ? row.ospite_id : row.id;
    if (!ospiteId || !month) return;
    const mesi = entity === "pagamenti" ? (row._blocco_mesi || {}) : (row.blocco_posto_mesi || {});
    const current = !!mesi[month];
    try {
      await api.post(`/ospiti/${ospiteId}/blocco-posto`, { mese: month, value: !current });
      qc.invalidateQueries({ queryKey: ["mod", entity] });
      qc.invalidateQueries({ queryKey: ["mod", "ospiti"] });
      qc.invalidateQueries({ queryKey: ["osp-badge"] });
    } catch (e) { toast.error(e.response?.data?.detail || "Errore aggiornamento"); }
  };
  const toggleRegistro = async (row) => {
    const ospiteId = entity === "pagamenti" ? row.ospite_id : row.id;
    if (!ospiteId || !month) return;
    const mesi = entity === "pagamenti" ? (row._registro_mesi || {}) : (row.registro_mesi || {});
    const current = !!mesi[month];
    try {
      await api.post(`/ospiti/${ospiteId}/registro`, { mese: month, value: !current });
      qc.invalidateQueries({ queryKey: ["mod", entity] });
      qc.invalidateQueries({ queryKey: ["mod", "ospiti"] });
      qc.invalidateQueries({ queryKey: ["osp-badge"] });
    } catch (e) { toast.error(e.response?.data?.detail || "Errore aggiornamento"); }
  };
  const requestToggle = (kind, row, currentlyChecked) => {
    if (currentlyChecked) { setToggleConfirm({ kind, row }); return; }
    if (kind === "blocco") toggleBloccoPosto(row); else toggleRegistro(row);
  };
  const runToggleConfirm = () => {
    const tc = toggleConfirm; setToggleConfirm(null);
    if (!tc) return;
    if (tc.kind === "blocco") toggleBloccoPosto(tc.row); else toggleRegistro(tc.row);
  };
  const selectAllRegistro = async () => {
    if (!month || !filtered.length) return;
    try {
      await Promise.all(filtered.map((row) => {
        const ospiteId = entity === "pagamenti" ? row.ospite_id : row.id;
        return ospiteId ? api.post(`/ospiti/${ospiteId}/registro`, { mese: month, value: true }) : null;
      }).filter(Boolean));
      qc.invalidateQueries({ queryKey: ["mod", entity] });
      qc.invalidateQueries({ queryKey: ["mod", "ospiti"] });
      qc.invalidateQueries({ queryKey: ["osp-badge"] });
      toast.success("Registro: selezionati tutti per il mese");
    } catch { toast.error("Errore aggiornamento"); }
  };
  const deselectAllRegistro = async () => {
    if (!month || !filtered.length) return;
    try {
      await Promise.all(filtered.map((row) => {
        const ospiteId = entity === "pagamenti" ? row.ospite_id : row.id;
        return ospiteId ? api.post(`/ospiti/${ospiteId}/registro`, { mese: month, value: false }) : null;
      }).filter(Boolean));
      qc.invalidateQueries({ queryKey: ["mod", entity] });
      qc.invalidateQueries({ queryKey: ["mod", "ospiti"] });
      qc.invalidateQueries({ queryKey: ["osp-badge"] });
      toast.success("Registro: deselezionati tutti per il mese");
    } catch { toast.error("Errore aggiornamento"); }
  };

  const lfVal = (f) => lf[f.key] ?? (f.type === "select" ? f.options[0][0] : f.type === "struttura" ? "all" : false);
  const filtered = rows.filter((r) => {
    if (filterFn && !filterFn(r)) return false;
    if (soloMancanti && !docMancanti(r).length) return false;
    if (config.listFilters && !config.listFilters.every((f) => f.match(r, lfVal(f)))) return false;
    if (!search) return true;
    return JSON.stringify(r).toLowerCase().includes(search.toLowerCase());
  });
  const _sortKey = config.sortKey || config.columns?.[0]?.key;
  filtered.sort((a, b) => {
    // Le rinunce (caparra trattenuta) NON seguono l'ordine alfabetico: sempre in fondo.
    const ra = isRinuncia(a) ? 1 : 0, rb = isRinuncia(b) ? 1 : 0;
    if (ra !== rb) return ra - rb;
    if (!_sortKey) return 0;
    return String(a[_sortKey] ?? "").localeCompare(String(b[_sortKey] ?? ""), "it", { numeric: true, sensitivity: "base" });
  });
  const delRow = rows.find((r) => r.id === delId);

  const exportColumns = visibleCols.map((c) => ({
    header: c.label,
    accessor: (r) => {
      const v = c.compute ? c.compute(r) : r[c.key];
      if (c.struct) return structMap[v]?.nome || "";
      if (c.eur) return fmtEur(v);
      if (c.date) return v ? fmtDate(v) : "";
      return v ?? "";
    },
  }));
  const exportSubtitle = selectedStructure === "all" ? "Tutte le strutture" : (structMap[selectedStructure]?.nome || "");
  const exportColor = selectedStructure === "all" ? "#1f7a5c" : (structMap[selectedStructure]?.colore || "#1f7a5c");

  const cell = (row, col) => {
    if (col.render) return col.render(row);
    const v = col.compute ? col.compute(row) : row[col.key];
    if (config.showDischargeBadge && col.dischargeName) {
      const ps = row._periodi && row._periodi.length ? row._periodi : null;
      const badge = row._dimesso
        ? <span className="ml-2 inline-block text-[11px] font-semibold rounded-full px-2 py-0.5 bg-amber-100 text-amber-700 border border-amber-300" data-testid={`rette-dimesso-${row.id}`}>Dimesso {fmtDate(row._dimesso)}</span>
        : (ps && ps.length > 1 ? <span className="ml-2 inline-block text-[11px] font-semibold rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-700 border border-emerald-300" data-testid={`rette-riammesso-${row.id}`}>Riammesso</span> : null);
      const name = row._nome_live || v;
      const clickable = row.ospite_id
        ? <button onClick={() => navigate(`/ospiti/${row.ospite_id}`)} className="font-semibold text-primary hover:underline text-left" data-testid={`rette-ospite-link-${row.id}`}>{name}</button>
        : <span className="font-medium">{name}</span>;
      return <span>{clickable}{badge}</span>;
    }
    if (col.editNum) {
      const input = <input type="number" defaultValue={v ?? ""} key={`${row.id}-${col.key}-${v}`}
        onBlur={(e) => { if (String(e.target.value) !== String(v ?? "")) saveNum(row, col, e.target.value); }}
        className="w-24 border rounded-md px-2 py-1 text-sm tabnum focus:border-primary outline-none" data-testid={`editnum-${col.key}-${row.id}`} />;
      if (col.key === "retta_prevista" && row.proporzionale && row.giorni_presenza != null && row.giorni_mese) {
        return (
          <div className="flex flex-col gap-0.5" data-testid={`retta-prop-${row.id}`}>
            {input}
            <span className="text-[10px] font-semibold text-amber-700 whitespace-nowrap" title={`Retta proporzionata: ${row.giorni_presenza} giorni di presenza su ${row.giorni_mese}. Retta intera: ${fmtEur(row.retta_intera)}`}>
              {row.giorni_presenza}/{row.giorni_mese} gg · intera {fmtEur(row.retta_intera)}
            </span>
          </div>
        );
      }
      return input;
    }
    if (col.statusToggle) return <button onClick={() => cycleStatus(row, col)} data-testid={`toggle-stato-${row.id}`} title="Clicca per cambiare stato" className="cursor-pointer"><StatusBadge type="stato" value={v} /></button>;
    if (col.struct) {
      const sid = (config.structByMonth && month) ? structForMonth(row, month) : v;
      const s = structMap[sid];
      return (
        <div>
          <span className="inline-flex items-center gap-1.5 font-semibold"><span className="inline-block h-2.5 w-2.5 rounded-full shrink-0" style={{ background: s?.colore || "#94a3b8" }} />{s?.nome || "—"}</span>
          {entity === "ospiti" && row.camera && <span className="block text-[9px] font-semibold text-blue-600 leading-tight mt-0.5" data-testid={`osp-camera-${row.id}`}>{row.camera}</span>}
        </div>
      );
    }
    if (col.structs) {
      const ids = (row.structure_ids && row.structure_ids.length) ? row.structure_ids : (row.structure_id ? [row.structure_id] : []);
      const names = ids.map((id) => structMap[id]?.nome).filter(Boolean);
      const all = (structures || []).filter((s) => s.id !== "all");
      if (names.length && names.length === all.length) return <span className="font-semibold text-primary">Tutte le strutture</span>;
      return <span className="text-xs font-semibold">{names.join(", ") || "—"}</span>;
    }
    if (config.detailBase && (col.key === "cognome" || col.key === "nome")) {
      const inf = entity === "ospiti" && col.key === "cognome" ? infMap[row.id] : null;
      const INF_LABEL = EVENTO_LABEL;
      const infNome = inf ? (inf.tipo === "altro" && (inf.tipo_altro || "").trim() ? inf.tipo_altro.trim() : (INF_LABEL[inf.tipo] || (inf.febbre ? "Febbre" : "Evento sanitario"))) : "";
      const infCol = inf ? eventoColor(inf.tipo) : null;
      return (
        <div className="relative">
          <button onClick={() => navigate(`${config.detailBase}/${row.id}`)}
            className="font-semibold text-primary hover:underline text-left" data-testid={`open-${row.id}-${col.key}`}>{v ?? "—"}</button>
          {col.key === "cognome" && <IdBadge id={row.id} className="block mt-0.5 text-[8px]" />}
          {col.key === "cognome" && <div><DocBadges r={row} /></div>}
          {inf && (
            <>
              <div className="h-4" />
              <span className={`absolute left-0 bottom-0 inline-flex items-center gap-1 rounded px-1.5 py-[1px] text-[9px] font-bold leading-tight whitespace-nowrap`} style={{ background: infCol.light, color: infCol.txt }} data-testid={`osp-malattia-badge-${row.id}`}>
                <span className="inline-block w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: infCol.bg }} />
                {infNome}{inf.temperatura ? ` ${inf.temperatura}°` : ""}{inf.isolamento ? " · isolamento" : ""}
              </span>
            </>
          )}
        </div>
      );
    }
    if (col.presenzaToggle) {
      const opts = [["presente", "Presente", "#15803d"], ["uscita", "Uscito", "#a16207"], ["ospedale", "Ricoverato", "#dc2626"]];
      return (
        <div className="flex flex-col gap-1">
          {opts.map(([val, lab, color]) => {
            const active = (row.presenza || "presente") === val;
            return (
              <button key={val} type="button" onClick={() => setStatoRow(row, val)} data-testid={`presenza-${val}-${row.id}`}
                className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors whitespace-nowrap text-center ${active ? "text-white" : "bg-white text-muted-foreground hover:bg-secondary"}`}
                style={active ? { backgroundColor: color, borderColor: color } : { borderColor: "var(--gr-border)" }}>{lab}</button>
            );
          })}
        </div>
      );
    }
    if (col.docStato) {
      if (!docObbl) return <span className="text-xs text-muted-foreground">…</span>;
      const m = docMancanti(row);
      if (!m.length) return <span className="inline-block rounded-full px-2.5 py-1 text-xs font-bold bg-emerald-100 text-emerald-700" data-testid={`osp-stato-completo-${row.id}`}>Completo</span>;
      return <div className="flex flex-wrap gap-1">{m.map((l) => <span key={l} className="inline-block rounded-full px-2.5 py-0.5 text-[11px] font-bold whitespace-nowrap bg-red-100 text-red-700" data-testid={`osp-doc-mancante-${row.id}-${l.split(" ")[1].toLowerCase()}`}>{l}</span>)}</div>;
    }
    if (col.rettaBadge) {
      const paid = v === "pagato";
      const badge = <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-bold ${!v ? "bg-secondary text-muted-foreground" : paid ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`} data-testid={`retta-stato-${row.id}`}>{!v ? "—" : paid ? "Pagato" : "Non pagato"}</span>;
      if (config.rosterRette) return <button type="button" title="Clicca per cambiare stato" onClick={() => upsertRetta(row, { stato: paid ? "da incassare" : "pagato" })} className="cursor-pointer">{badge}</button>;
      return badge;
    }
    if (col.bloccoPosto) {
      const mesi = entity === "pagamenti" ? (row._blocco_mesi || {}) : (row.blocco_posto_mesi || {});
      const checked = !!(month && mesi[month]);
      return <div className="flex justify-center"><Checkbox checked={checked} onCheckedChange={() => requestToggle("blocco", row, checked)} data-testid={`blocco-posto-${row.id}`} title="Blocco posto (solo questo mese)" /></div>;
    }
    if (col.registroToggle) {
      const mesi = entity === "pagamenti" ? (row._registro_mesi || {}) : (row.registro_mesi || {});
      const checked = !!(month && mesi[month]);
      return <div className="flex justify-center"><Checkbox checked={checked} onCheckedChange={() => requestToggle("registro", row, checked)} data-testid={`registro-${row.id}`} title="Registrato sul registro ospiti (questo mese)" /></div>;
    }
    if (col.badge) return <StatusBadge type={col.badge} value={v} />;
    if (col.eur) return <span className="tabnum">{v == null ? "—" : fmtEur(v)}</span>;
    if (col.date) return fmtDate(v);
    return v ?? "—";
  };

  const isOperatori = entity === "operatori";
  const opsNoEmail = isOperatori ? filtered.filter((r) => !String(r.email || "").trim()) : [];
  const opsWithEmail = isOperatori ? filtered.filter((r) => String(r.email || "").trim()) : [];
  const isOspiti = entity === "ospiti";
  const ospNoEmail = isOspiti ? filtered.filter((r) => !String(r.email || "").trim()) : [];

  return (
    <div>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-head font-bold">{config.title}</h1>
          <p className="text-sm text-muted-foreground"><span className="font-bold text-foreground tabnum">{filtered.length}</span> {config.title.toLowerCase()} in totale</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
          <div className="hidden md:inline-flex rounded-lg border overflow-hidden mr-1">
            <button onClick={() => setView("lista")} title="Vista lista" className={`px-2.5 h-9 grid place-items-center ${view === "lista" ? "bg-primary text-white" : "bg-white"}`} data-testid={`view-lista-${entity}`}><List size={16} /></button>
            <button onClick={() => setView("esplosa")} title="Vista esplosa" className={`px-2.5 h-9 grid place-items-center ${view === "esplosa" ? "bg-primary text-white" : "bg-white"}`} data-testid={`view-esplosa-${entity}`}><LayoutGrid size={16} /></button>
          </div>
          <div className="relative flex-1 sm:flex-none min-w-[140px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Cerca..." value={search} onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-full sm:w-56 h-11 sm:h-10" data-testid={`search-${entity}`} />
          </div>
          {entity === "ospiti" && docObbl && (
            <button type="button" onClick={() => setSoloMancanti((v) => !v)} className={`h-11 sm:h-10 px-3 rounded-lg border text-sm font-semibold transition-colors ${soloMancanti ? "bg-red-600 text-white border-red-600" : "bg-white text-red-700 border-red-200 hover:bg-red-50"}`} data-testid="filtro-doc-mancanti">
              Documenti mancanti ({rows.filter((r) => (!filterFn || filterFn(r)) && docMancanti(r).length).length})
            </button>
          )}
          <ExportButton filename={entity} title={config.title} subtitle={exportSubtitle} columns={exportColumns} rows={filtered} color={exportColor} testid={`export-${entity}`} />
          {isOperatori && (
            <Button variant="outline" onClick={() => setPinAllOpen(true)} data-testid="invia-pin-tutti" className="font-semibold gr-mobile-tap">
              <Send size={16} className="mr-1.5" /> Invia PIN a tutti
            </Button>
          )}
          <Button onClick={openNew} data-testid={`new-${entity}`} className="font-semibold gr-mobile-tap flex-1 sm:flex-none">
            <Plus size={18} className="mr-1" /> {config.newLabel}
          </Button>
        </div>
      </div>

      {config.listFilters && (
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid={`listfilters-${entity}`}>
          {config.listFilters.map((f) => f.type === "select" ? (
            <Select key={f.key} value={lfVal(f)} onValueChange={(v) => setLf((p) => ({ ...p, [f.key]: v }))}>
              <SelectTrigger className="w-52" data-testid={`listfilter-${f.key}`}><SelectValue /></SelectTrigger>
              <SelectContent>{f.options.map(([val, lab]) => <SelectItem key={val} value={val}>{lab}</SelectItem>)}</SelectContent>
            </Select>
          ) : f.type === "struttura" ? (
            <Select key={f.key} value={lfVal(f)} onValueChange={(v) => setLf((p) => ({ ...p, [f.key]: v }))}>
              <SelectTrigger className="w-52" data-testid={`listfilter-${f.key}`}><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutte le strutture</SelectItem>{structures.filter((s) => s.id !== "all").map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}</SelectContent>
            </Select>
          ) : (
            <button key={f.key} onClick={() => setLf((p) => ({ ...p, [f.key]: !lfVal(f) }))} data-testid={`listfilter-${f.key}`}
              className={`px-3 h-9 rounded-lg border text-sm font-semibold transition-colors ${lfVal(f) ? "bg-amber-500 text-white border-amber-500" : "hover:bg-secondary"}`}>{f.label}</button>
          ))}
        </div>
      )}

      {isOperatori && opsNoEmail.length > 0 && (
        <div className="flex items-start gap-2 mb-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm" data-testid="ops-email-mancante-banner">
          <AlertTriangle size={17} className="text-amber-600 mt-0.5 shrink-0" />
          <div>
            <b>{opsNoEmail.length} operator{opsNoEmail.length === 1 ? "e" : "i"} senza email</b> — aggiungi l'indirizzo (matita) per poter inviare il PIN via email.
            <div className="text-amber-800/80 mt-0.5">{opsNoEmail.map((r) => [r.cognome, r.nome].filter(Boolean).join(" ")).join(", ")}</div>
          </div>
        </div>
      )}

      {isOspiti && ospNoEmail.length > 0 && (
        <div className="flex items-start gap-2 mb-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm" data-testid="ospiti-email-mancante-banner">
          <AlertTriangle size={17} className="text-amber-600 mt-0.5 shrink-0" />
          <div>
            <b>{ospNoEmail.length} ospit{ospNoEmail.length === 1 ? "e" : "i"} senza email</b> — aggiungi l'indirizzo del familiare (matita) per poter inviare le comunicazioni via email.
            <div className="text-amber-800/80 mt-0.5">{ospNoEmail.map((r) => [r.cognome, r.nome].filter(Boolean).join(" ")).join(", ")}</div>
          </div>
        </div>
      )}

      {bulkOn && (
        <div className="flex items-center gap-2 mb-4 flex-wrap rounded-lg border bg-secondary/30 px-3 py-2" data-testid="bulk-pay-bar">
          <span className="text-sm font-semibold text-muted-foreground">Selezione rapida pagamenti:</span>
          <button onClick={() => setSel(filtered.map((r) => r.id))} className="h-9 px-3 rounded-lg border text-sm font-semibold hover:bg-secondary" data-testid="bulk-select-all">Seleziona tutte</button>
          <button onClick={() => setSel(filtered.filter((r) => r.stato !== "pagato").map((r) => r.id))} className="h-9 px-3 rounded-lg border text-sm font-semibold hover:bg-secondary" data-testid="bulk-select-unpaid">Solo non pagate</button>
          {sel.length > 0 && <button onClick={() => setSel([])} className="h-9 px-3 rounded-lg border text-sm font-semibold hover:bg-secondary" data-testid="bulk-clear">Deseleziona ({sel.length})</button>}
          <div className="flex-1" />
          <Button onClick={bulkMarkPaid} disabled={sel.length === 0} className="font-semibold bg-emerald-600 hover:bg-emerald-700" data-testid="bulk-mark-paid">
            <Check size={16} className="mr-1" /> Segna come pagati{sel.length > 0 ? ` (${sel.length})` : ""}
          </Button>
        </div>
      )}

      <div className={`gr-card overflow-hidden ${view === "esplosa" ? "hidden" : "hidden md:block"}`}>
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/60">
              {bulkOn && <TableHead className="w-10">
                <Checkbox checked={filtered.length > 0 && sel.length === filtered.length} onCheckedChange={(v) => setSel(v ? filtered.map((r) => r.id) : [])} data-testid="bulk-header-checkbox" />
              </TableHead>}
              <TableHead className="font-bold w-12 text-muted-foreground">#</TableHead>
              {visibleCols.map((c) => <TableHead key={c.key} className="font-bold">{c.registroToggle ? (
                <div className="flex flex-col items-center leading-none">
                  <span>{c.label}</span>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <button type="button" onClick={selectAllRegistro} className="text-[10px] font-semibold text-primary hover:underline" data-testid="registro-select-all">tutti</button>
                    <span className="text-[10px] text-muted-foreground">·</span>
                    <button type="button" onClick={deselectAllRegistro} className="text-[10px] font-semibold text-muted-foreground hover:underline hover:text-foreground" data-testid="registro-deselect-all">nessuno</button>
                  </div>
                </div>
              ) : c.label}</TableHead>)}
              {config.eventActions && <TableHead className="font-bold">Eventi</TableHead>}
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableRow><TableCell colSpan={99} className="text-center py-10 text-muted-foreground">Caricamento...</TableCell></TableRow>}
            {!isLoading && filtered.length === 0 && <TableRow><TableCell colSpan={99} className="text-center py-10 text-muted-foreground">Nessun record</TableCell></TableRow>}
            {filtered.map((row, _i) => (
              <TableRow key={row.id} data-testid={`row-${entity}-${row.id}`} className={isRinuncia(row) ? "bg-red-50 border-l-4 border-l-red-500" : (bulkOn && sel.includes(row.id) ? "bg-emerald-50" : "")}>
                {bulkOn && <TableCell><Checkbox checked={sel.includes(row.id)} onCheckedChange={() => toggleSel(row.id)} data-testid={`bulk-check-${row.id}`} /></TableCell>}
                <TableCell className="text-muted-foreground tabnum font-semibold align-top">{isRinuncia(row) ? "" : _i + 1}</TableCell>
                {visibleCols.map((c) => <TableCell key={c.key} className="font-medium align-top">{cell(row, c)}</TableCell>)}
                {config.eventActions && <TableCell className="whitespace-nowrap">
                  {config.eventActions.map((a) => <Button key={a.type} size="sm" variant="outline" className="mr-1 h-7 px-2 text-xs" onClick={() => setQuick({ type: a.type, row })} data-testid={`quick-${a.type}-${row.id}`}>{a.label}</Button>)}
                </TableCell>}
                <TableCell className="text-right whitespace-nowrap">
                  {isRinuncia(row) && <span className="mr-2 inline-block text-[11px] font-semibold rounded-full px-2 py-0.5 bg-red-100 text-red-700 border border-red-300" data-testid={`badge-rinuncia-${row.id}`}>Rinuncia · caparra trattenuta</span>}
                  {canDischarge && !isRinuncia(row) && (() => {
                    const ps = (row.periodi && row.periodi.length) ? row.periodi : [{ ingresso: row[startField], dimissione: row[dischField] }];
                    if (row[dischField]) return <span className="mr-2 inline-block text-[11px] font-semibold rounded-full px-2 py-0.5 bg-amber-100 text-amber-700 border border-amber-300" data-testid={`badge-dimesso-${row.id}`}>{entity === "operatori" ? "Cessato" : row.deceduto ? "Dimesso / deceduto/a" : "Dimesso"} {fmtDate(row[dischField])}</span>;
                    if (ps.length > 1) return <span className="mr-2 inline-block text-[11px] font-semibold rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-700 border border-emerald-300" data-testid={`badge-riammesso-${row.id}`}>{entity === "operatori" ? "Riassunto" : "Riammesso"} {fmtDate(ps[ps.length - 1].ingresso)}</span>;
                    return null;
                  })()}
                  {config.detailBase && <Button size="sm" variant="ghost" onClick={() => navigate(`${config.detailBase}/${row.id}`)} data-testid={`scheda-${row.id}`}><FolderOpen size={15} className="text-primary" /></Button>}
                  {isOperatori && <Button size="sm" variant="ghost" onClick={() => doCheckinPdf(row)} title="Scarica il PDF con le credenziali (per il check-in)" data-testid={`checkin-pdf-${row.id}`}><FileText size={15} className="text-primary" /></Button>}
                  {isOperatori && <Button size="sm" variant="ghost" onClick={() => setPinRow(row)} title={String(row.email || "").trim() ? "Invia il PIN via email all'operatore" : "Email mancante: aggiungila per inviare il PIN"} data-testid={`invia-pin-${row.id}`}>{String(row.email || "").trim() ? <KeyRound size={15} className="text-primary" /> : <AlertTriangle size={15} className="text-amber-500" />}</Button>}
                  <Button size="sm" variant="ghost" onClick={() => openEdit(row)} data-testid={`edit-${row.id}`}><Pencil size={15} /></Button>
                  {canDischarge && <Button size="sm" variant="ghost" onClick={() => openDischarge(row)} title={row[dischField] ? `${entity === "operatori" ? "Cessato" : "Dimesso"} il ${fmtDate(row[dischField])}` : (entity === "operatori" ? "Cessa operatore" : "Dimetti ospite")} data-testid={`dimetti-${row.id}`}><UserMinus size={15} className={row[dischField] ? "text-amber-600" : "text-muted-foreground"} /></Button>}
                  {entity !== "pagamenti" && <Button size="sm" variant="ghost" onClick={() => setDelId(row.id)} data-testid={`delete-${row.id}`}><Trash2 size={15} className="text-destructive" /></Button>}
                </TableCell>
              </TableRow>
            ))}
            {!isLoading && filtered.length > 0 && visibleCols.some((c) => c.eur || c.editNum) && (
              <TableRow className="bg-secondary/70 font-bold border-t-2" data-testid={`totali-${entity}`}>
                {bulkOn && <TableCell />}
                <TableCell className="font-extrabold uppercase text-xs">Totale</TableCell>
                {visibleCols.map((c) => (
                  <TableCell key={c.key} className="tabnum font-extrabold">
                    {(c.eur || c.editNum) ? fmtEur(filtered.reduce((s, r) => s + (Number(c.compute ? c.compute(r) : r[c.key]) || 0), 0)) : ""}
                  </TableCell>
                ))}
                {config.eventActions && <TableCell />}
                <TableCell />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 ${view === "esplosa" ? "" : "md:hidden"}`} data-testid={`grid-${entity}`}>
          {!isLoading && filtered.length === 0 && <div className="text-sm text-muted-foreground">Nessun record</div>}
          {filtered.map((row, _i) => (
            <div key={row.id} className={`gr-card p-3.5 ${isRinuncia(row) ? "bg-red-50 border-l-4 border-l-red-500" : ""}`} data-testid={`card-${entity}-${row.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-start gap-2 min-w-0">
                  {bulkOn && <Checkbox checked={sel.includes(row.id)} onCheckedChange={() => toggleSel(row.id)} data-testid={`bulk-card-check-${row.id}`} />}
                  <span className="h-6 w-6 shrink-0 rounded-full bg-secondary grid place-items-center text-xs font-bold tabnum">{_i + 1}</span>
                  <div className="min-w-0">
                    <button onClick={() => config.detailBase ? navigate(`${config.detailBase}/${row.id}`) : openEdit(row)} className="font-bold text-primary hover:underline text-left text-base block" data-testid={`card-open-${row.id}`}>{[row.cognome, row.nome].filter(Boolean).join(" ") || "—"}</button>
                    <IdBadge id={row.id} className="block mt-0.5 text-[9px]" />
                    <div><DocBadges r={row} /></div>
                    {isRinuncia(row) && <span className="inline-block mt-1 text-[10px] font-semibold rounded-full px-2 py-0.5 bg-red-100 text-red-700 border border-red-300" data-testid={`card-badge-rinuncia-${row.id}`}>Rinuncia · caparra trattenuta</span>}
                  </div>
                </div>
                <div className="flex shrink-0 gap-1">
                  {config.detailBase && <Button size="icon" variant="ghost" className="h-10 w-10" onClick={() => navigate(`${config.detailBase}/${row.id}`)} data-testid={`card-scheda-${row.id}`}><FolderOpen size={16} className="text-primary" /></Button>}
                  {isOperatori && <Button size="icon" variant="ghost" className="h-10 w-10" onClick={() => doCheckinPdf(row)} title="PDF credenziali (check-in)" data-testid={`card-checkin-pdf-${row.id}`}><FileText size={16} className="text-primary" /></Button>}
                  {isOperatori && <Button size="icon" variant="ghost" className="h-10 w-10" onClick={() => setPinRow(row)} title="Invia il PIN via email" data-testid={`card-invia-pin-${row.id}`}>{String(row.email || "").trim() ? <KeyRound size={16} className="text-primary" /> : <AlertTriangle size={16} className="text-amber-500" />}</Button>}
                  <Button size="icon" variant="ghost" className="h-10 w-10" onClick={() => openEdit(row)}><Pencil size={16} /></Button>
                  {entity !== "pagamenti" && <Button size="icon" variant="ghost" className="h-10 w-10" onClick={() => setDelId(row.id)}><Trash2 size={16} className="text-destructive" /></Button>}
                </div>
              </div>
              <div className="space-y-1.5">
                {visibleCols.filter((c) => c.key !== "cognome" && c.key !== "nome").map((c) => (
                  <div key={c.key} className="flex items-center justify-between gap-2 text-sm"><span className="text-muted-foreground text-xs">{c.label}</span><span className="font-medium text-right">{cell(row, c)}</span></div>
                ))}
              </div>
              {config.eventActions && <div className="mt-2 pt-2 border-t flex flex-wrap gap-1.5">
                {config.eventActions.map((a) => <Button key={a.type} size="sm" variant="outline" className="h-9 px-3 text-xs" onClick={() => setQuick({ type: a.type, row })}>{a.label}</Button>)}
              </div>}
            </div>
          ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifica" : config.newLabel}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {config.fields.filter((f) => (!f.perm || permOk(f.perm)) && (!f.createOnly || !editing)).map((f) => (
              <div key={f.key} className={f.type === "textarea" ? "col-span-2" : ""}>
                <Label className="text-xs font-semibold mb-1 block">{f.label}{f.req && " *"}</Label>
                {f.type === "textarea" ? (
                  <Textarea value={form[f.key] || ""} onChange={(e) => setF(f.key, e.target.value)} data-testid={`field-${f.key}`} />
                ) : f.type === "ospite" ? (
                  <Select value={form.ospite_id || ""} onValueChange={(v) => {
                    const o = ospiti.find((x) => x.id === v);
                    setForm((p) => ({ ...p, ospite_id: v, ospite_nome: o ? `${o.nome} ${o.cognome}` : "", structure_id: p.structure_id || o?.structure_id }));
                  }}>
                    <SelectTrigger data-testid={`field-${f.key}`}><SelectValue placeholder="Seleziona ospite" /></SelectTrigger>
                    <SelectContent>{ospiti.map((o) => <SelectItem key={o.id} value={o.id}>{o.nome} {o.cognome} — cam. {o.camera}</SelectItem>)}</SelectContent>
                  </Select>
                ) : f.type === "fornitore" ? (
                  <Select value={form[f.key] || ""} onValueChange={(v) => setF(f.key, v)}>
                    <SelectTrigger data-testid={`field-${f.key}`}><SelectValue placeholder={fornitori.length ? "Seleziona fornitore" : "Nessun fornitore — aggiungili in Fornitori"} /></SelectTrigger>
                    <SelectContent>
                      {form[f.key] && !fornitori.some((x) => x.nome === form[f.key]) && <SelectItem value={form[f.key]}>{form[f.key]}</SelectItem>}
                      {[...fornitori].sort((a, b) => (a.nome || "").localeCompare(b.nome || "", "it")).filter((x) => entity === "utenze" ? x.categoria === "utenze" : (entity === "spese" ? (x.categoria || "") !== "utenze" : true) && (!form.categoria || !x.categoria || x.categoria === form.categoria)).map((x) => <SelectItem key={x.id} value={x.nome}>{x.nome}{x.categoria ? ` · ${x.categoria}` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : f.type === "select" ? (
                  <Select value={form[f.key] || ""} onValueChange={(v) => setF(f.key, v)}>
                    <SelectTrigger data-testid={`field-${f.key}`}><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent>
                      {f.options.map((o) => {
                        const [val, lab] = Array.isArray(o) ? o : [o, o];
                        return <SelectItem key={val} value={val}>{lab}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                ) : f.type === "strutture" ? (
                  <div className="flex flex-wrap gap-1.5" data-testid={`field-${f.key}`}>
                    {structures.filter((s) => s.id !== "all").map((s) => {
                      const cur = form.structure_ids || (form.structure_id ? [form.structure_id] : []);
                      const sel = cur.includes(s.id);
                      return <button type="button" key={s.id} data-testid={`strutture-${s.id}`} onClick={() => setForm((p) => { const c = p.structure_ids || (p.structure_id ? [p.structure_id] : []); const next = sel ? c.filter((x) => x !== s.id) : [...c, s.id]; return { ...p, structure_ids: next, structure_id: next[0] || null }; })}
                        className={`text-xs font-semibold rounded-full border px-3 py-1 transition-colors ${sel ? "bg-primary text-white border-primary" : "hover:bg-secondary"}`}>{s.nome}</button>;
                    })}
                    <button type="button" data-testid="strutture-tutte" onClick={() => setForm((p) => { const all = structures.filter((s) => s.id !== "all").map((s) => s.id); const isAll = (p.structure_ids || []).length === all.length; const next = isAll ? [] : all; return { ...p, structure_ids: next, structure_id: next[0] || null }; })}
                      className="text-xs font-semibold rounded-full border border-dashed px-3 py-1 hover:bg-secondary">Tutte le strutture</button>
                  </div>
                ) : f.type === "struttura" ? (
                  <Select value={form.structure_id || ""} onValueChange={(v) => setF("structure_id", v)}>
                    <SelectTrigger data-testid={`field-${f.key}`}><SelectValue placeholder="Seleziona struttura" /></SelectTrigger>
                    <SelectContent>{structures.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}</SelectContent>
                  </Select>
                ) : f.type === "operatore_responsabile" ? (
                  <OperatoreResponsabileSelect structureId={form.structure_id} value={form.responsabile_id} testid={`field-${f.key}`}
                    onChange={({ id: rid, nome }) => setForm((p) => ({ ...p, responsabile_id: rid, responsabile_nome: nome }))} />
                ) : f.type === "struttura_split" ? (
                  <Select value={form.structure_id || ""} onValueChange={(v) => setF("structure_id", v)}>
                    <SelectTrigger data-testid={`field-${f.key}`}><SelectValue placeholder="Seleziona struttura" /></SelectTrigger>
                    <SelectContent>
                      {structures.length > 1 && <SelectItem value="__all__" data-testid="struttura-split-all">Tutte le strutture (dividi in parti uguali)</SelectItem>}
                      {structures.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                    value={form[f.key] || ""} onChange={(e) => setF(f.key, e.target.value)} data-testid={`field-${f.key}`} />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="save-btn" className="font-semibold">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toggleConfirm} onOpenChange={() => setToggleConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confermi la rimozione della spunta?</AlertDialogTitle>
            <AlertDialogDescription>
              {toggleConfirm?.kind === "blocco"
                ? "Stai togliendo il Blocco posto per questo mese. Confermi?"
                : "Stai togliendo la registrazione sul registro per questo ospite. Confermi?"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel data-testid="toggle-confirm-annulla">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); runToggleConfirm(); }} data-testid="toggle-confirm-ok">Sì, togli</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!scadConfirm} onOpenChange={() => setScadConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>⚠️ Confermi la scadenza contratto?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>Stai modificando la <b>data di scadenza del contratto</b>. Verifica che sia corretta prima di salvare.</p>
                <div className="flex items-center gap-2 rounded-lg border bg-secondary/40 px-3 py-2">
                  <span className="text-muted-foreground">{scadConfirm?.oldS ? fmtDate(scadConfirm.oldS) : "Nessuna"}</span>
                  <span className="font-bold">→</span>
                  <span className="font-bold text-primary">{scadConfirm?.newS ? fmtDate(scadConfirm.newS) : "Nessuna"}</span>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel data-testid="scad-confirm-annulla">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); const p = scadConfirm?.payload; setScadConfirm(null); if (p) persistEdit(p); }} data-testid="scad-confirm-ok">Sì, conferma data</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!delId} onOpenChange={() => setDelId(null)}>        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{canDischarge ? "⚠️ Stai ELIMINANDO definitivamente" : "Confermi l'eliminazione?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {canDischarge ? (
                <>
                  {entity === "operatori" ? "L'operatore" : "L'ospite"} <b>{[delRow?.cognome, delRow?.nome].filter(Boolean).join(" ") || ""}</b> verrà rimosso da <b>TUTTI i mesi</b>. Usa questa opzione <b>solo per inserimenti errati</b> (doppioni, creato per sbaglio).<br /><br />
                  Se invece {entity === "operatori" ? "l'operatore ha cessato il servizio" : "l'ospite se n'è andato o è deceduto"}, premi <b>"Dimetti invece"</b>: resterà nello storico con il badge {entity === "operatori" ? "\"Cessato\"" : "\"Dimesso\""}.
                </>
              ) : "Il record verrà archiviato (soft delete) e potrà essere ripristinato dall'Admin."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            {canDischarge && <Button variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50" onClick={() => { const r = delRow; setDelId(null); if (r) openDischarge(r); }} data-testid="delete-dimetti-instead"><UserMinus size={15} className="mr-1" /> Dimetti invece</Button>}
            <AlertDialogAction onClick={doDelete} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-delete">Elimina comunque</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pinRow} onOpenChange={() => !pinBusy && setPinRow(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Invia il PIN via email</AlertDialogTitle>
            <AlertDialogDescription>
              Verrà inviata un'email a <b>{[pinRow?.cognome, pinRow?.nome].filter(Boolean).join(" ") || "l'operatore"}</b>
              {pinRow?.email ? <> all'indirizzo <b>{pinRow.email}</b></> : <span className="text-destructive"> — attenzione: nessuna email registrata su questo operatore</span>}.<br /><br />
              L'email contiene <b>solo il PIN</b> e il <b>link</b> per accedere a Gestione Residenze. L'operatore potrà poi cambiare il PIN in autonomia da «Il mio account».
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel disabled={pinBusy}>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); doSendPin(); }} disabled={pinBusy || !pinRow?.email} data-testid="confirm-invia-pin">{pinBusy ? "Invio…" : "Invia PIN"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={pinAllOpen} onOpenChange={() => !pinAllBusy && setPinAllOpen(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Invia il PIN a tutti gli operatori</AlertDialogTitle>
            <AlertDialogDescription>
              Verrà inviata un'email con il PIN a <b>{opsWithEmail.length} operator{opsWithEmail.length === 1 ? "e" : "i"}</b> che {opsWithEmail.length === 1 ? "ha" : "hanno"} un'email registrata.
              {opsNoEmail.length > 0 && <><br /><br /><span className="text-amber-700"><b>{opsNoEmail.length}</b> ver{opsNoEmail.length === 1 ? "rà" : "ranno"} saltat{opsNoEmail.length === 1 ? "o" : "i"}</span> perché senza email: {opsNoEmail.map((r) => [r.cognome, r.nome].filter(Boolean).join(" ")).join(", ")}.</>}
              <br /><br />Ogni email contiene solo il PIN e il link per accedere.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel disabled={pinAllBusy}>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); doSendPinAll(); }} disabled={pinAllBusy || opsWithEmail.length === 0} data-testid="confirm-invia-pin-tutti">{pinAllBusy ? "Invio…" : `Invia a ${opsWithEmail.length}`}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pinAllResult} onOpenChange={() => setPinAllResult(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Invio PIN completato</AlertDialogTitle>
            <AlertDialogDescription>
              <b className="text-emerald-700">{pinAllResult?.inviate || 0} PIN inviati</b> su {pinAllResult?.totale || 0} operatori.
              {(pinAllResult?.senza_email?.length > 0) && <><br /><br /><b>Senza email ({pinAllResult.senza_email.length}):</b> {pinAllResult.senza_email.join(", ")}</>}
              {(pinAllResult?.errori?.length > 0) && <><br /><br /><span className="text-destructive"><b>Errori ({pinAllResult.errori.length}):</b> {pinAllResult.errori.join("; ")}</span></>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setPinAllResult(null)} data-testid="pin-tutti-result-ok">Chiudi</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!dischargeRow} onOpenChange={() => setDischargeRow(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{entity === "operatori" ? "Cessa operatore" : "Dimetti ospite"} {dischargeRow ? `— ${dischargeRow.nome || ""} ${dischargeRow.cognome || ""}` : ""}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-1">
            {dischargeRow?.[dischField] && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 space-y-2.5" data-testid="dimetti-stato">
                <p className="text-sm text-amber-800">Attualmente <b>{entity === "operatori" ? "cessato" : "dimesso"} il {fmtDate(dischargeRow[dischField])}</b>. Puoi aggiornare la data qui sotto, oppure:</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button variant="outline" size="sm" className="w-full sm:w-auto border-amber-300 text-amber-800 hover:bg-amber-100" onClick={() => doDischarge(true)} data-testid="dimetti-annulla"><RotateCcw size={15} className="mr-1" /> Annulla {entity === "operatori" ? "cessazione" : "dimissione"}</Button>
                  <Button variant="outline" size="sm" className="w-full sm:w-auto border-amber-300 text-amber-800 hover:bg-amber-100" onClick={doReadmit} disabled={!dischargeDate} data-testid="dimetti-riammetti">{entity === "operatori" ? "Riassumi" : "Riammetti"} (nuovo periodo)</Button>
                </div>
              </div>
            )}
            <p className="text-sm text-muted-foreground">Indica il giorno. Con l'uscita la persona resta visibile fino a quel mese e sparisce dai mesi successivi{entity === "operatori" ? "" : " (retta proporzionale)"}. Con la {entity === "operatori" ? "riassunzione" : "riammissione"} viene creato un nuovo periodo di presenza da quella data.</p>
            <div>
              <Label className="text-sm font-semibold">Data {entity === "operatori" ? "cessazione" : "dimissione"}</Label>
              <div className="grid grid-cols-3 gap-2 mt-1.5">
                <Select value={dischG} onValueChange={setDischG}>
                  <SelectTrigger data-testid="dimetti-giorno"><SelectValue placeholder="Giorno" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0")).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={dischM} onValueChange={setDischM}>
                  <SelectTrigger data-testid="dimetti-mese"><SelectValue placeholder="Mese" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"].map((m, i) => <SelectItem key={i} value={String(i + 1).padStart(2, "0")}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={dischA} onValueChange={setDischA}>
                  <SelectTrigger data-testid="dimetti-anno"><SelectValue placeholder="Anno" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {Array.from({ length: 8 }, (_, i) => String(new Date().getFullYear() - 5 + i)).map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {entity === "ospiti" && (
              <label className="flex items-center gap-2.5 rounded-lg border border-slate-300 bg-slate-50 px-3 py-2.5 cursor-pointer" data-testid="dimetti-deceduto-label">
                <input type="checkbox" checked={deceduto} onChange={(e) => setDeceduto(e.target.checked)} className="h-4 w-4 accent-slate-700" data-testid="dimetti-deceduto-check" />
                <span className="text-sm font-semibold text-slate-800">Deceduto/a</span>
              </label>
            )}
          </div>
          <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-2">
            <Button variant="outline" className="w-full sm:w-auto" onClick={() => setDischargeRow(null)}>Chiudi</Button>
            <Button onClick={() => doDischarge(false)} disabled={!dischargeDate} data-testid="dimetti-conferma" className="w-full sm:w-auto font-semibold">{dischargeRow?.[dischField] ? (dischargeRow?.deceduto !== deceduto ? "Aggiorna" : "Aggiorna data") : "Conferma"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {config.eventActions && <QuickEventDialog open={!!quick} type={quick?.type} ospite={quick?.row}
        onOpenChange={() => setQuick(null)} onSaved={() => setQuick(null)} />}
    </div>
  );
}
